import { Doc } from '@dadohr/shared/domainTypes/document';
import { ITimelineItemNote } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import { AlertCircle, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { InfoBox } from '../../../../../components/InfoBox';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useTranslation } from '../../../../../i18next';
import {
  isDadoEmailAddress,
  useCurrentUser
} from '../../../../../services/currentUser';
import {
  addTimelineItemNote,
  deleteTimelineItemNote,
  useTimelineItemNotes
} from '../../../../../services/experiences/notes';
import { hasCurrentUserExperienceLevelScopes } from '../../../../../services/permissions';
import { toMoment } from '../../../../../services/time';

const Container = styled('div')((p) => ({
  padding: p.theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(3),
  width: '100%',
  maxWidth: 800
}));

const AddNoteRow = styled('div')((p) => ({
  display: 'flex',
  gap: p.theme.spacing(2),
  alignItems: 'flex-start'
}));

const NoteList = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const NoteRow = styled('div')((p) => ({
  display: 'flex',
  gap: p.theme.spacing(2),
  alignItems: 'flex-start',
  '& .note-delete': { opacity: 0 },
  '&:hover .note-delete': { opacity: 1 }
}));

const NoteDate = styled('div')((p) => ({
  minWidth: 100,
  color: p.theme.custom.colors.gray.w500,
  fontSize: p.theme.custom.fontSize.s,
  paddingTop: 2
}));

const NoteContent = styled('div')(() => ({
  flex: 1
}));

const NoteText = styled('div')(() => ({
  wordBreak: 'break-word'
}));

const NoteMeta = styled('span')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  fontSize: p.theme.custom.fontSize.s,
  marginLeft: p.theme.spacing(1)
}));

const getAuthorDisplayName = (note: ITimelineItemNote): string => {
  if (isDadoEmailAddress(note.createdByEmail)) {
    return 'Dado Customer Support';
  }
  return note.createdByName;
};

const DeleteNoteDialog = ({
  open,
  onClose,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('Delete note?')}</DialogTitle>
      <DialogContent>
        <InfoBox color="red" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={16} />
          <span>{t('Be careful! This cannot be undone.')}</span>
        </InfoBox>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending={t('Deleting')}
          onClick={() => onConfirm().then(onClose)}
        >
          {t('Delete')}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const NoteItem = ({
  note,
  currentUserId,
  dateFormat,
  showDate
}: {
  note: Doc<ITimelineItemNote>;
  currentUserId: string;
  dateFormat: string;
  showDate: boolean;
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbarError } = useSnackbar();
  const deleteDialog = useDialogState();

  const created = toMoment(note.data.createdAt);
  const date = created.format(dateFormat);
  const time = created.format('H:mm');
  const author = getAuthorDisplayName(note.data);

  return (
    <NoteRow>
      <NoteDate>{showDate ? date : ''}</NoteDate>
      <NoteContent>
        <NoteText>
          {note.data.text}
          <NoteMeta>
            ({time}, {author})
          </NoteMeta>
        </NoteText>
      </NoteContent>
      <ButtonWithoutPropagation
        className="note-delete"
        variant="text"
        size="small"
        onClick={() => deleteDialog.openDialog()}
        title={t('Delete note')}
      >
        <Trash2 size={14} />
      </ButtonWithoutPropagation>
      {deleteDialog.dialogOpen && (
        <DeleteNoteDialog
          open
          onClose={deleteDialog.closeDialog}
          onConfirm={async () => {
            await deleteTimelineItemNote(note, currentUserId).catch((e) => {
              enqueueSnackbarError(t('Failed to delete note. Please try again.'));
              throw e;
            });
          }}
        />
      )}
    </NoteRow>
  );
};

export const TimelineItemNotesTab = ({
  orgId,
  experienceTemplateId,
  itemId,
  itemType
}: {
  orgId: string;
  experienceTemplateId: string;
  itemId: string;
  itemType: ITimelineItemNote['itemType'];
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { id: currentUserId, email, firstName, lastName } = currentUser;
  const dateFormat = currentUser.org.dateFormats.MMDDYYYY;
  const canCreate = hasCurrentUserExperienceLevelScopes(currentUser, experienceTemplateId, ['edit']);
  const [notes] = useTimelineItemNotes(orgId, experienceTemplateId, itemId);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbarError } = useSnackbar();

  const handleSave = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    await addTimelineItemNote(
      orgId,
      experienceTemplateId,
      itemId,
      itemType,
      text.trim(),
      currentUserId,
      email,
      `${firstName} ${lastName}`.trim()
    )
      .then(() => setText(''))
      .catch(() =>
        enqueueSnackbarError(t('Failed to save note. Please try again.'))
      )
      .finally(() => setSaving(false));
  };

  return (
    <Container>
      {canCreate && (
        <AddNoteRow>
          <TextField
            placeholder={t('Add note')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.metaKey || e.ctrlKey) && handleSave()}
            variant="outlined"
            style={{ flex: 1 }}
            multiline
            maxRows={6}
            inputProps={{ maxLength: 800 }}
            InputProps={{ style: { borderRadius: 4 } }}
            helperText={`${text.length} / 800`}
          />
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '1px' }}
            onClick={handleSave}
            disabled={!text.trim() || saving}
          >
            {saving ? t('Saving') : t('Save')}
          </Button>
        </AddNoteRow>
      )}
      {notes && notes.length > 0 && (
        <NoteList>
          {notes.map((note, i) => (
            <NoteItem
              key={note.id}
              note={note}
              currentUserId={currentUserId}
              dateFormat={dateFormat}
              showDate={
                i === 0 ||
                toMoment(note.data.createdAt).format(dateFormat) !==
                  toMoment(notes[i - 1].data.createdAt).format(dateFormat)
              }
            />
          ))}
        </NoteList>
      )}
      {notes && notes.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t('No notes yet.')}
        </Typography>
      )}
    </Container>
  );
};
